import type { IGraphClient } from '../../../src/services/GraphService';
import type { IWorkspace } from '../../api/src/workspace';
import { getToken, resumeAfterSignIn } from '../auth';
import { GraphAuditLog } from '../GraphAuditLog';
import { findListId } from '../lists/findList';
import { ListStore } from '../lists/ListStore';
import { RESTRICTED_GROUP, RESTRICTED_HISTORY, RESTRICTED_LIBRARY, RESTRICTED_LISTS } from './schemas';

/**
 * SharePoint's own API, for what Microsoft Graph can't do: a site group, and lists with their own
 * permissions. Needs the person to be a site owner; asked for from a click (a page redirect).
 */
export class SharePointSite {
  constructor(private readonly _siteUrl: string) {}

  private get _scope(): string[] {
    return [`${new URL(this._siteUrl).origin}/AllSites.FullControl`];
  }

  public async call<T>(path: string, method: string = 'GET', body?: unknown): Promise<T> {
    const token: string = await getToken(this._scope, true, true);
    const r: Response = await fetch(`${this._siteUrl.replace(/\/$/, '')}/_api/${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json;odata=nometadata', 'Content-Type': 'application/json;odata=nometadata' },
      body: body === undefined ? undefined : JSON.stringify(body)
    });
    if (r.status === 401 || r.status === 403) {
      throw new Error('Only an owner of this SharePoint site can do this. Ask a site owner.');
    }
    if (!r.ok) {
      throw Object.assign(new Error(`SharePoint answered HTTP ${r.status}.`), { statusCode: r.status });
    }
    const text: string = await r.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /** Asks for the permission now, from the click. */
  public async ask(resume: { kind: string; [k: string]: unknown }): Promise<void> {
    resumeAfterSignIn(resume);
    await getToken(this._scope, true, true);
  }

  public async group(): Promise<number | undefined> {
    try {
      return (await this.call<{ Id: number }>(`web/sitegroups/getbyname('${encodeURIComponent(RESTRICTED_GROUP)}')`)).Id;
    } catch (e) {
      if ((e as { statusCode?: number }).statusCode === 404) {
        return undefined;
      }
      throw e;
    }
  }

  public async members(groupId: number): Promise<{ id: number; name: string; email: string }[]> {
    const r = await this.call<{ value: { Id: number; Title: string; Email: string }[] }>(`web/sitegroups(${groupId})/users`);
    return r.value.map(u => ({ id: u.Id, name: u.Title, email: u.Email }));
  }

  public async addMember(groupId: number, email: string): Promise<void> {
    await this.call(`web/sitegroups(${groupId})/users`, 'POST', { LoginName: `i:0#.f|membership|${email}` });
  }

  public async removeMember(groupId: number, userId: number): Promise<void> {
    await this.call(`web/sitegroups(${groupId})/users/removebyid(${userId})`, 'POST');
  }

  /** SharePoint's built-in permission levels by type, so it works whatever the site's language: 5 is Full Control, 6 is Edit. */
  private async _roleId(type: 5 | 6): Promise<number> {
    return (await this.call<{ Id: number }>(`web/roledefinitions/getbytype(${type})`)).Id;
  }

  /**
   * Gives a list (or library) its own permissions: the restricted group may edit, the site's owners
   * keep full control, and nobody else sees it. Items inside follow the list. Safe to run again:
   * a list that already has its own permissions gets the two grants again (adding one twice changes nothing),
   * so a setup stopped halfway is finished.
   */
  public async restrict(listId: string, groupId: number): Promise<void> {
    const list: string = `web/lists(guid'${listId}')`;
    if (!(await this.isRestricted(listId))) {
      await this.call(`${list}/breakroleinheritance(copyRoleAssignments=false,clearSubscopes=true)`, 'POST');
    }
    const owners: number = (await this.call<{ Id: number }>('web/associatedownergroup')).Id;
    await this.call(`${list}/roleassignments/addroleassignment(principalid=${owners},roledefid=${await this._roleId(5)})`, 'POST');
    await this.call(`${list}/roleassignments/addroleassignment(principalid=${groupId},roledefid=${await this._roleId(6)})`, 'POST');
  }

  /** Whether a list keeps its own permissions. */
  public async isRestricted(listId: string): Promise<boolean> {
    return (await this.call<{ HasUniqueRoleAssignments: boolean }>(`web/lists(guid'${listId}')?$select=HasUniqueRoleAssignments`)).HasUniqueRoleAssignments;
  }
}

/**
 * Sets up restricted files once: a "Restricted files" group (the person setting it up is its first
 * member), a library and lists of their own and their own history, each open only to that group
 * and the site's owners. Safe to run again (finds what's already there).
 */
export async function setUpRestricted(
  graph: IGraphClient, manageGraph: IGraphClient, workspace: IWorkspace, siteUrl: string, me: string, onProgress: (text: string) => void
): Promise<{ driveId: string; libraryName: string }> {
  const sp: SharePointSite = new SharePointSite(siteUrl);

  onProgress('Creating the Restricted files group…');
  let groupId: number | undefined = await sp.group();
  if (groupId === undefined) {
    groupId = (await sp.call<{ Id: number }>('web/sitegroups', 'POST', { Title: RESTRICTED_GROUP, Description: 'People who can open restricted immigration files (refugee and private sponsorship, and any the firm marks).' })).Id;
  }
  const members = await sp.members(groupId);
  if (!members.some(m => m.email.toLowerCase() === me.toLowerCase())) {
    await sp.addMember(groupId, me);
  }

  onProgress(`Creating the ${RESTRICTED_LIBRARY} library…`);
  let library: { id: string; drive?: { id: string } } | undefined;
  try {
    library = await graph.api(`/sites/${workspace.siteId}/lists/${encodeURIComponent(RESTRICTED_LIBRARY)}?$expand=drive($select=id)`).get();
  } catch (e) {
    if ((e as { statusCode?: number }).statusCode !== 404) {
      throw e;
    }
  }
  if (!library) {
    const created: { id: string } = await manageGraph.api(`/sites/${workspace.siteId}/lists`).post({ displayName: RESTRICTED_LIBRARY, list: { template: 'documentLibrary' } });
    library = await graph.api(`/sites/${workspace.siteId}/lists/${created.id}?$expand=drive($select=id)`).get();
  }
  const driveId: string = library!.drive?.id ?? '';
  if (!driveId) {
    throw new Error('The Restricted files library was created, but its files couldn\'t be found. Try again.');
  }

  const listIds: string[] = [library!.id];
  for (const schema of RESTRICTED_LISTS) {
    onProgress(`Creating the ${schema.title} list…`);
    listIds.push(await new ListStore(graph, manageGraph, workspace.siteId, schema).ensure());
  }
  onProgress('Creating the restricted history…');
  const history: GraphAuditLog = new GraphAuditLog(graph, manageGraph, workspace.siteId, siteUrl, RESTRICTED_HISTORY);
  await history.ensure();
  const historyList: string | undefined = await findListId(graph, workspace.siteId, [RESTRICTED_HISTORY.title, RESTRICTED_HISTORY.urlName]);
  if (!historyList) {
    throw new Error('The restricted history list was created but can\'t be found. Try setting up again.');
  }
  listIds.push(historyList);

  let n: number = 0;
  for (const id of listIds) {
    onProgress(`Giving them their own permissions (${++n} of ${listIds.length})…`);
    await sp.restrict(id, groupId);
  }
  return { driveId, libraryName: RESTRICTED_LIBRARY };
}
